import { ThemeEvents } from '@theme/events';

class GiftOfferProgress extends HTMLElement {
  #listeners;
  #request;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    // Add responses can contain only the newly added items. Ask Shopify for the
    // complete count, including gifts on other pages and any partial stock adds.
    document.addEventListener(ThemeEvents.cartUpdate, this.#refresh, options);
    document.addEventListener(ThemeEvents.cartError, this.#refresh, options);
    document.addEventListener(ThemeEvents.discountUpdate, this.#refresh, options);
    window.addEventListener('pageshow', this.#refresh, options);
    window.addEventListener('focus', this.#refresh, options);
    document.addEventListener('visibilitychange', this.#onVisibility, options);
    this.querySelector('[data-gift-progress-retry]')?.addEventListener('click', this.#refresh, options);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
  }

  #onVisibility = () => {
    if (document.visibilityState === 'visible') this.#refresh();
  };

  #refresh = async () => {
    const status = this.querySelector('[data-gift-progress-status]');
    const progress = this.querySelector('progress');
    const retry = this.querySelector('[data-gift-progress-retry]');
    if (!status || !progress || !retry) return;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    const timeout = setTimeout(() => request.abort(), 12000);
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.dataset.refreshUrl, {
        credentials: 'same-origin', cache: 'no-store', signal: request.signal,
      });
      if (!response.ok) throw new Error('Offer progress unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const updated = html.querySelector('gift-offer-progress');
      const updatedStatus = updated?.querySelector('[data-gift-progress-status]');
      const updatedProgress = updated?.querySelector('progress');
      if (updated?.dataset.giftRange !== this.dataset.giftRange || !updatedStatus || !updatedProgress) {
        throw new Error('Offer progress missing');
      }
      // An older request must never overwrite a newer cart update.
      if (request !== this.#request || request.signal.aborted || !this.isConnected) return;
      if (status.textContent !== updatedStatus.textContent) status.textContent = updatedStatus.textContent;
      status.dataset.quantity = updatedStatus.dataset.quantity;
      // Picture slots are optional; sync them when both sides render them.
      const slots = this.querySelector('[data-gift-progress-slots]');
      const updatedSlots = updated.querySelector('[data-gift-progress-slots]');
      if (slots && updatedSlots && typeof updatedSlots.innerHTML === 'string' && slots.innerHTML !== updatedSlots.innerHTML) {
        slots.innerHTML = updatedSlots.innerHTML;
      }
      progress.max = updatedProgress.max;
      progress.value = updatedProgress.value;
      progress.hidden = false;
      retry.hidden = true;
    } catch {
      if (request !== this.#request || !this.isConnected) return;
      status.textContent = 'Check your basket for offer progress.';
      delete status.dataset.quantity;
      progress.hidden = true;
      retry.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (request === this.#request) this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('gift-offer-progress')) {
  customElements.define('gift-offer-progress', GiftOfferProgress);
}
